import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import DOMPurify from "dompurify";

/**
 * VAPT Security Enhancements:
 * - Input sanitization for all user inputs (XSS prevention)
 * - URL validation for website fields
 * - Email validation with sanitization
 * - Phone number sanitization
 * - Numeric input validation with limits
 * - DOMPurify integration for HTML sanitization
 * - Removed console.log/error statements (information leakage prevention)
 * - Form validation before submission
 */

// SVG Images
import Back from "../../assets/svg/back.svg";

import { ReactComponent as SearchIc } from "../../assets/svg/searchIc_s.svg";
import { ReactComponent as LeadOwner } from "../../assets/svg/lead_owner.svg";

import { ReactComponent as InstaIc } from "../../assets/svg/insta_ic.svg";
import { ReactComponent as XIC } from "../../assets/svg/x-ic.svg";
import { ReactComponent as LinIC } from "../../assets/svg/linkden_ic.svg";
import { ReactComponent as YoutIC } from "../../assets/svg/youtub_ic.svg";
import { ReactComponent as FasIC } from "../../assets/svg/fas_ic.svg";

//PNG Images
import Profile from "../../assets/png/profile.png";

//API
import {
  createLeadsApi,
  getLeadDetailsApi,
  getLeadsOwnerApi,
  getWebScrapingApi,
  UpdateLeadsApi,
  updateWebScrapingApi,
} from "./leads-api";
import SearchWithFreeTextAPI from "../common/searchbox-free-text";
import SearchWithAPI from "../common/searchbox";
import WebsiteDetailsWidget from "../common/webscraping";
import { getProfileDetailsApi } from "../common/common-api";
import {
  sanitizeInput,
  sanitizeTextarea,
  validateUrl,
  sanitizeEmail,
  sanitizePhoneNumber,
  sanitizeNumericInput,
} from "../common/security-utils.js";

const LeadsAdd = () => {
  //useLocation
  const location = useLocation();
  const { id } = location.state || {};
  const [isSaving, setIsSaving] = useState(false);
  const [aboutList, setAboutList] = useState([]);

  // VAPT Security: ID validation and rate limiting
  const isValidId = (id) => {
    if (!id) return false;
    const idStr = String(id);
    return /^[a-zA-Z0-9_-]+$/.test(idStr) && idStr.length <= 50;
  };

  const MAX_NUMERIC_VALUE = 999999999;
  const MAX_STRING_LENGTH = 200;

  // VAPT Security: Sanitization helper functions for web scraping data
  const sanitizeArray = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((item) => {
      if (typeof item === "string") {
        return sanitizeInput(item);
      } else if (typeof item === "object" && item !== null) {
        return sanitizeObject(item);
      }
      return item;
    });
  };

  const sanitizeObject = (obj) => {
    if (!obj || typeof obj !== "object") return {};
    const sanitized = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (typeof value === "string") {
        sanitized[key] = sanitizeInput(value);
      } else if (Array.isArray(value)) {
        sanitized[key] = sanitizeArray(value);
      } else if (typeof value === "object" && value !== null) {
        sanitized[key] = sanitizeObject(value);
      } else {
        sanitized[key] = value;
      }
    });
    return sanitized;
  };

  const sanitizeWebScrapDetails = (details) => {
    if (!details || typeof details !== "object") return {};
    return sanitizeObject(details);
  };

  //API URL
  const apiUrl = process.env.REACT_APP_API_URL;

  const apiAccUrl = process.env.REACT_APP_ADMIN_API_URL;
  const ignoredDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
  ];

  const handleEmailChange = (e) => {
    const value = e.target.value;
    const sanitized = sanitizeEmail(value);
    setEmail(sanitized || value); // Keep original if not valid yet (user still typing)

    // Check if email contains "@"
    if (value.includes("@")) {
      const domain = value.split("@")[1]?.trim().toLowerCase();

      // If domain exists and not ignored → fetch or do something
      if (domain && !ignoredDomains.includes(domain)) {
        // Validate domain before setting
        if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) {
          setWebsiteUrl(domain);
        }
      }
    }
  };
  const [isInputDisabled, setIsInputDisabled] = useState(false);

  // Social media icons mapping
  const socialIcons = {
    linkedin: <LinIC />,
    facebook: <FasIC />,
    x: <XIC />,
    twitter: <XIC />,
    instagram: <InstaIc />,
    youtube: <YoutIC />,
  };

  //
  const [isLoading, setIsLoading] = useState(false);
  const [mailChecked, setMailChecked] = useState(false);
  const [isActive, setIsActive] = useState(false);
  //Navigate
  const navigate = useNavigate();
  //Input Values
  const [webScrapDetails, setWebScrapDetails] = useState({});
  const [serviceList, setServicesList] = useState([]);
  const [industriesList, setIndustriesList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [teamList, setTeamsList] = useState([]);
  const [partnerList, setParnterList] = useState([]);
  const [payLinkDetails, setPageDetails] = useState({});
  const [payLinkList, setPageLinList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [peopleList, setPeopleList] = useState([]);
  const [careersList, setCareersList] = useState({});

  const [errorMsg, setErrorMsg] = useState({
    url: "",
  });

  const sections = [
    { label: "Details", value: webScrapDetails },
    { label: "Services", value: serviceList },
    { label: "Industries", value: industriesList },
    { label: "Products", value: productsList },
    { label: "About", value: webScrapDetails?.about ?? aboutList },
    { label: "Teams", value: teamList },
    { label: "Partners", value: partnerList },
    { label: "Pages Link", value: payLinkList },
    { label: "Clients", value: clientList },
    { label: "People", value: peopleList },
    { label: "Careers", value: careersList },
  ];

  const [street, setStreet] = useState("");
  const [cityOption, setCityOption] = useState();
  const [stateOption, setStateOption] = useState();
  const [zipCode, setZipCode] = useState("");
  const [countryOption, setCountryOption] = useState();
  const [description, setDescription] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [leadOwner, setLeadOwner] = useState(null);
  const [leadAssignee, setLeadAssignee] = useState("");
  const [leadAssigneeId, setLeadAssigneeId] = useState(0);
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [secondaryMobileNumber, setSecondaryMobileNumber] = useState("");
  const [fax, setFax] = useState("");
  const [company, setCompany] = useState("");
  const [leadSource, setLeadSource] = useState(null);
  const [industry, setIndustry] = useState(null);
  const [leadStatus, setLeadStatus] = useState(null);
  const [employees, setEmployees] = useState("");
  const [annualReve, setAnnualReve] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const normalizeKey = (key = "") => {
    return key
      .replace(".com", "")
      .replace("www.", "")
      .replace("https://", "")
      .replace("http://", "")
      .split(".")[0]
      .toLowerCase();
  };

  const createSocialLinks = (socialMediaObj = {}) => {
    return Object.entries(socialMediaObj).map(([key, url]) => {
      const normalized = normalizeKey(key); // convert key to instagram/facebook etc.

      return {
        name: sanitizeInput(extractHandle(url)), // VAPT Security: Sanitize handle
        icon: socialIcons[normalized] || null,
        url: sanitizeInput(url), // VAPT Security: Sanitize URL
      };
    });
  };

  const extractHandle = (url = "") => {
    if (!url) return "";

    // VAPT Security: Validate URL length
    if (url.length > MAX_STRING_LENGTH) {
      url = url.substring(0, MAX_STRING_LENGTH);
    }

    // Remove protocol & www
    let clean = url.replace(/https?:\/\/(www\.)?/i, "").replace(/\/$/, "");

    if (clean.includes("linkedin.com")) {
      const parts = clean.split("/");
      return parts[2] || ""; // after /company/
    }

    // Default logic for facebook/instagram/twitter
    clean = clean.replace(/\.[a-z]+/i, "");
    return clean.split("/")[1] || clean;
  };

  const [socialLinks, setSocialLinks] = useState([]);

  // const socialLinks = [
  //   {
  //     name: "@organicdelightbakery",
  //     icon: <InstaIc />, // or <InstaIc /> if SVG component
  //   },
  //   {
  //     name: "@delightcakes",
  //     icon: <YoutIC />,
  //   },
  //   {
  //     name: "@treatshouse",
  //     icon: <XIC />,
  //   },
  //   {
  //     name: "@freshbakerylife",
  //     icon: <FasIC />,
  //   },
  //   {
  //     name: "@freshbakerylife",
  //     icon: <LinIC />,
  //   },
  // ];

  //Lead Sourse Options
  const handleLeadSourceDropdownChange = (option) => {
    if (option?.label) {
      setLeadSource(sanitizeInput(option.label));
    }
  };

  const handleLeadSourceOnChange = (term) => {
    setLeadSource(sanitizeInput(term));
  };

  //Lead Status
  const handleLeadStatusDropdownChange = (option) => {
    if (option?.label) {
      setLeadStatus(sanitizeInput(option.label));
    }
  };

  const handleLeadStatusOnChange = (term) => {
    setLeadStatus(sanitizeInput(term));
  };

  //Industry

  const handleIndustryDropdownChange = (option) => {
    if (option?.label) {
      setIndustry(sanitizeInput(option.label));
    }
  };

  const handleIndustryOnChange = (term) => {
    setIndustry(term);
  };

  //City
  const handleCityDropdownChange = (option) => {
    setCityOption(option);
    setStateOption({ label: option.state_name, value: option.state_id });
    setCountryOption({ label: option.country_name, value: option.country_id });
  };

  //State

  const handleStateDropdownChange = (option) => {
    setStateOption(option);
    setCountryOption({ label: option.country_name, value: option.country_id });
    setCityOption(null);
  };

  //Country

  const handleCountryDropdownChange = (option) => {
    setCountryOption(option);
    setCityOption(null);
    setStateOption(null);
  };

  // Lead Owner
  const handleLeadAssigneeDropdownChange = (option) => {
    setLeadAssignee(option?.label || "");
    setLeadAssigneeId(option?.value || 0);
  };

  const handleLeadAssigneeOnChange = (term) => {
    setLeadAssignee(term);
    setLeadAssigneeId(0);
  };

  //Validations
  const [validations, setValidations] = useState({
    first_name: "",
    last_name: "",
    lead_owner_id: "",

    email: "",
    contact_number1: "",
    contact_number2: "",

    fax: "",
    website: "",

    lead_source: "",
    lead_status: "",

    company_name: "",
    industry: "",
    emp_count: "",

    city_id: "",
    state_id: "",
    country_id: "",
    address: "",
    zip_code: "",

    description: "",
  });

  //Save API
  // const submitLead = async () => {
  //   const dataBeforeSanitize = {
  //     image: base64Image,
  //     first_name: firstName ?? "",
  //     last_name: lastName ?? "",
  //     email: email ?? "",
  //     contact_number1: mobileNumber ?? "",
  //     contact_number2: secondaryMobileNumber ?? "",
  //     fax: fax ?? "",
  //     website: website ?? "",
  //     lead_source: leadSource,
  //     lead_status: leadStatus,
  //     company_name: company ?? "",
  //     industry: industry,
  //     emp_count: employees ?? "",
  //     city_id: cityOption != null ? cityOption.value : "",
  //     state_id: stateOption != null ? stateOption.value : "",
  //     country_id: countryOption != null ? countryOption.value : "",
  //     zip_code: zipCode ?? "",
  //     description: description ?? "",
  //     address: street ?? "",
  //     url: websiteUrl,
  //     annual_revenue: annualReve,
  //     email_marketing: mailChecked
  //   };

  //   const dataAfterSanitize = {};
  //   Object.entries(dataBeforeSanitize).forEach(([key, value]) => {
  //     if (typeof value === "boolean" || typeof value === "number" || Array.isArray(value) || key === "assignee") {
  //       dataAfterSanitize[key] = value;
  //     } else {
  //       dataAfterSanitize[key] = DOMPurify.sanitize(value);
  //     }
  //   });
  //   let response;
  //   if (id != null) {
  //     response = await UpdateLeadsApi(dataAfterSanitize, id);
  //   } else {
  //     response = await createLeadsApi(dataAfterSanitize);
  //   }

  //   if (response.status === 200) {
  //     if (response.data.success === true) {
  //       navigate("/leads/list");
  //       // if (base64FileList.length > 0) {
  //       //   uploadAttachment(response.data.id);
  //       // }
  //       // onSuccess(response.data);
  //     }
  //   }
  //   if (response.status === 400) {
  //     new Promise((resolve, reject) => {
  //       const validationTemp = {
  //         first_name: "",
  //         last_name: "",
  //         lead_owner_id: "",
  //         email: "",
  //         contact_number1: "",
  //         contact_number2: "",
  //         fax: "",
  //         website: "",
  //         lead_source: "",
  //         lead_status: "",
  //         company_name: "",
  //         industry: "",
  //         emp_count: "",
  //         city_id: "",
  //         state_id: "",
  //         country_id: "",
  //         zip_code: "",
  //         address: "",

  //         description: "",
  //       };

  //       Object.entries(response.data.error).forEach(([key, value]) => {
  //         console.log(`${key}: ${value}`);
  //         if (key in validationTemp) {
  //           validationTemp[key] = Array.isArray(value) && value.length > 0 ? value[0] : "";
  //         }
  //       });

  //       console.log("validationTemp...", validationTemp);
  //       setValidations(validationTemp);
  //     });
  //   }
  // };

  const submitLead = async () => {
    if (isLoading) return;
    setIsSaving(true); // start loader

    // First Name Validation (required)
    if (!firstName?.trim()) {
      setValidations((prev) => ({
        ...prev,
        first_name: "First name is required",
      }));
      setIsSaving(false);
      return;
    }

    // Validate website URL if provided
    if (websiteUrl && !validateUrl(websiteUrl)) {
      setValidations((prev) => ({
        ...prev,
        website:
          "Please enter a valid URL (must start with http:// or https://)",
      }));
      setIsSaving(false);
      return;
    }

    // Validate email if provided
    if (email && !sanitizeEmail(email)) {
      setValidations((prev) => ({
        ...prev,
        email: "Please enter a valid email address",
      }));
      setIsSaving(false);
      return;
    }

    const dataBeforeSanitize = {
      lead_owner_id: leadAssigneeId || null,
      image: base64Image || null,
      first_name: firstName?.trim() ?? "",
      last_name: lastName?.trim() || null,
      email: email?.trim() || null,
      contact_number1: mobileNumber?.trim() || null,
      contact_number2: secondaryMobileNumber?.trim() || null,
      fax: fax?.trim() || null,
      lead_source: leadSource || null,
      lead_status: leadStatus || null,
      company_name: company?.trim() || null,
      industry: industry || null,
      emp_count: employees ? sanitizeNumericInput(employees) : null,
      city_id: cityOption?.value || null,
      state_id: stateOption?.value || null,
      country_id: countryOption?.value || null,
      zip_code: zipCode?.trim() || null,
      description: description?.trim() || null,
      address: street?.trim() || null,
      website: websiteUrl?.trim() || null,
      webscrap: Object.keys(webScrapDetails).length > 0 ? true : false,
      annual_revenue: annualReve ? sanitizeNumericInput(annualReve) : null,
      email_marketing: mailChecked,
    };

    const dataAfterSanitize = {};
    Object.entries(dataBeforeSanitize).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        dataAfterSanitize[key] = null;
      } else if (
        typeof value === "boolean" ||
        typeof value === "number" ||
        Array.isArray(value) ||
        key === "assignee"
      ) {
        dataAfterSanitize[key] = value;
      } else {
        dataAfterSanitize[key] = DOMPurify.sanitize(value);
      }
    });

    try {
      let response;
      if (id != null) {
        // VAPT Security: Validate ID before API call
        if (!isValidId(id)) {
          setValidations((prev) => ({
            ...prev,
            general: "Invalid ID format",
          }));
          setIsSaving(false);
          return;
        }
        response = await UpdateLeadsApi(dataAfterSanitize, id);
      } else {
        response = await createLeadsApi(dataAfterSanitize);
      }

      if (
        (response?.status === 200 || response?.status === 201) &&
        response?.data?.success === true
      ) {
        if (
          Object.keys(webScrapDetails).length > 0 &&
          websiteUrl !== "" &&
          response.data.lead_id
        ) {
          updateWebScraping(response.data.lead_id);
        } else {
          navigate(-1);
        }
      } else if (
        response?.status === 400 ||
        response?.status === 422 ||
        response?.data?.success === false
      ) {
        const validationTemp = {};

        // VAPT Security: Sanitize validation error messages
        if (response?.data?.error) {
          Object.entries(response.data.error).forEach(([key, value]) => {
            validationTemp[key] =
              Array.isArray(value) && value.length > 0
                ? sanitizeInput(value[0])
                : typeof value === "string"
                ? sanitizeInput(value)
                : "";
          });
        }

        setValidations((prev) => ({ ...prev, ...validationTemp }));
      }
    } catch (err) {
      if (err?.response?.data?.error) {
        const validationTemp = {};
        Object.entries(err.response.data.error).forEach(([key, value]) => {
          validationTemp[key] =
            Array.isArray(value) && value.length > 0
              ? sanitizeInput(value[0])
              : typeof value === "string"
              ? sanitizeInput(value)
              : "";
        });
        setValidations((prev) => ({ ...prev, ...validationTemp }));
      }
    } finally {
      setIsSaving(false); // stop loader
    }
  };

  const updateWebScraping = async (id) => {
    try {
      // VAPT Security: Validate ID before API call
      if (!isValidId(id)) {
        setIsSaving(false);
        return;
      }
      let response = await updateWebScrapingApi(webScrapDetails, id);
      if (response.status === 200 && response.data.success === true) {
        navigate(-1);
      }
    } catch (err) {
      // Error handled internally
    } finally {
      setIsSaving(false); // stop loader
    }
  };

  const getProfileDetails = async () => {
    try {
      const response = await getProfileDetailsApi();
      if (response.status === 200 && response.data?.success) {
        const user = response.data.user;
        const fullName = `${user.first_name}`.trim();
        setLeadOwner({ name: fullName });
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (id == null) {
      getProfileDetails();
    }
  }, [id]);

  useEffect(() => {
    // Component initialization
    if (id != null) {
      // VAPT Security: Validate ID before API call
      if (isValidId(id)) {
        getLeadDetails();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getLeadDetails = async () => {
    try {
      // VAPT Security: Validate ID before API call
      if (!isValidId(id)) {
        return;
      }
      const response = await getLeadDetailsApi(id);
      if (response.status === true || response.lead != null) {
        const leadDetails = response.lead; // Get the first lead

        setFirstName(sanitizeInput(leadDetails.first_name ?? ""));
        setLastName(sanitizeInput(leadDetails.last_name ?? ""));
        setEmail(
          sanitizeEmail(leadDetails.email ?? "") || (leadDetails.email ?? ""),
        );
        setMobileNumber(sanitizePhoneNumber(leadDetails.contact_number1 ?? ""));
        setSecondaryMobileNumber(
          sanitizePhoneNumber(leadDetails.contact_number2 ?? ""),
        );
        setFax(sanitizeInput(leadDetails.fax ?? ""));
        setWebsiteUrl(leadDetails.website ?? "");
        setLeadSource(sanitizeInput(leadDetails.lead_source ?? ""));
        setLeadStatus(sanitizeInput(leadDetails.lead_status ?? ""));
        setLeadAssignee(sanitizeInput(leadDetails.lead_owner_name));
        setLeadAssigneeId(leadDetails.lead_owner_id);
        setLeadOwner({ name: sanitizeInput(leadDetails.lead_owner_name) });

        setCompany(sanitizeInput(leadDetails.company_name ?? ""));
        setIndustry(sanitizeInput(leadDetails.industry ?? ""));
        setEmployees(sanitizeNumericInput(leadDetails.emp_count ?? ""));

        setCityOption({
          label: sanitizeInput(leadDetails.city ?? ""),
          value: leadDetails.city_id ?? "",
        });
        setStateOption({
          label: sanitizeInput(leadDetails.state ?? ""),
          value: leadDetails.state_id ?? "",
        });
        setCountryOption({
          label: sanitizeInput(leadDetails.country ?? ""),
          value: leadDetails.country_id ?? "",
        });
        setZipCode(sanitizeInput(leadDetails.zip_code ?? ""));
        setStreet(sanitizeInput(leadDetails.address ?? ""));

        setDescription(sanitizeTextarea(leadDetails.description ?? ""));

        setProfileUrl(leadDetails.image ?? "");
        setAnnualReve(sanitizeNumericInput(leadDetails.annual_revenue ?? ""));
        setMailChecked(leadDetails.email_marketing ?? false);
        const url = leadDetails.website ?? "";
        setIsInputDisabled(url !== ""); // disable only when URL is NOT empty

        // VAPT Security: Sanitize social links and web scraping data
        setSocialLinks(createSocialEditLinks(leadDetails.social_links ?? {}));
        setWebScrapDetails(
          sanitizeWebScrapDetails(leadDetails.scrapping_details ?? {}),
        );
        setServicesList(sanitizeArray(leadDetails.services ?? []));
        setIndustriesList(sanitizeArray(leadDetails.industries ?? []));
        setProductsList(sanitizeArray(leadDetails.products ?? []));
        setTeamsList(sanitizeArray(leadDetails.teams ?? []));
        // setWebsiteUrl(leadDetails.scrapping_details.url ?? '');
        setAboutList(sanitizeArray(leadDetails.abouts ?? []));
        setParnterList(sanitizeArray(leadDetails.partners ?? []));
        setPageDetails(leadDetails.total_pages ?? 0);
        setPageLinList(sanitizeArray(leadDetails.pages ?? []));
        setClientList(sanitizeArray(leadDetails.clients ?? []));
        setPeopleList(sanitizeArray(leadDetails.people ?? []));
        setCareersList(sanitizeObject(leadDetails.careers ?? {}));
        // Lead details loaded successfully
      }
    } catch (err) {
      // Error handled internally
    }
  };
  const normalizeEditKey = (key = "") => {
    return key
      .replace(".com", "")
      .replace("www.", "")
      .split(".")[0]
      .toLowerCase();
  };

  const createSocialEditLinks = (socialLinksArray = []) => {
    return socialLinksArray.map((item) => {
      const normalized = normalizeEditKey(item.name);

      return {
        name: sanitizeInput("@" + extractEditHandle(item.link)), // VAPT Security: Sanitize formatted @handle
        icon: socialIcons[normalized] || null,
        url: sanitizeInput(item.link), // VAPT Security: Sanitize URL
      };
    });
  };

  const extractEditHandle = (url = "") => {
    if (!url) return "";

    // VAPT Security: Validate URL length
    if (url.length > MAX_STRING_LENGTH) {
      url = url.substring(0, MAX_STRING_LENGTH);
    }

    try {
      let clean = url.replace(/https?:\/\/(www\.)?/i, "").replace(/\/$/, "");

      // LinkedIn extraction
      if (clean.includes("linkedin.com")) {
        const parts = clean.split("/");
        return parts[2] || ""; // after /company/
      }

      // Normal social pages: instagram, facebook, twitter, youtube
      return clean.split("/")[1]?.replace("@", "") || clean;
    } catch {
      return "";
    }
  };
  const clearScrappedData = () => {
    setIsLoading(false);
    setIsInputDisabled(false);
    setSocialLinks([]);
    setAboutList([]);
    setWebScrapDetails({});
    setServicesList([]);
    setIndustriesList([]);
    setProductsList([]);
    setTeamsList([]);
    setParnterList([]);
    setPageDetails([]);
    setPageLinList([]);
    setClientList([]);
    setPeopleList([]);
    setCareersList([]);

    setWebsiteUrl(""); // clear input
    setErrorMsg({}); // clear errors
  };

  const getWebScrapDet = async () => {
    try {
      // Validate URL before making API call
      if (!websiteUrl) {
        setErrorMsg({ url: "Website URL is required" });
        return;
      }

      if (!validateUrl(websiteUrl)) {
        setErrorMsg({ url: "Please enter a valid URL (http:// or https://)" });
        return;
      }

      setIsLoading(true);
      setIsInputDisabled(true);
      setErrorMsg({
        url: "",
      });
      const bodyData = {
        url: websiteUrl,
      };

      const response = await getWebScrapingApi(bodyData);
      // Web scraping data fetched

      if (response.data.success === true) {
        let webScap = response.data.data ?? {};

        // VAPT Security: Sanitize all web scraping response data
        setSocialLinks(
          createSocialLinks(
            sanitizeObject(response.data.data?.social_links ?? {}),
          ),
        );
        setWebScrapDetails(sanitizeWebScrapDetails(webScap));
        setServicesList(sanitizeArray(webScap.services ?? []));
        setIndustriesList(sanitizeArray(webScap.industries ?? []));
        setProductsList(sanitizeArray(webScap.products ?? []));
        setTeamsList(sanitizeArray(webScap.team ?? []));
        setParnterList(sanitizeArray(webScap.partners ?? []));
        setPageDetails(webScap.total_pages ?? []);
        setPageLinList(sanitizeArray(webScap.pages ?? []));
        setClientList(sanitizeArray(webScap.clients ?? []));
        setPeopleList(sanitizeArray(webScap.people ?? []));
        setCareersList(sanitizeObject(webScap.careers ?? []));
      }
      if (response.status === 400) {
        const validationTemp = {
          url: "",
        };
        Object.entries(response.data.error).forEach(([key, value]) => {
          if (key in validationTemp) {
            validationTemp[key] =
              Array.isArray(value) && value.length > 0
                ? sanitizeInput(value[0])
                : "";
          }
        });
        setErrorMsg(validationTemp);
      }
    } catch (err) {
      // Error handled internally
    } finally {
      setIsLoading(false); // stop loader always
    }
  };

  //Choose Files

  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(Profile);
  const [base64Image, setBase64Image] = useState("");
  const [profileUrl, setProfileUrl] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    // VAPT Security: File validation
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"];

    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      alert("Only PNG, JPG, and JPEG files are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert("File size must be less than 10MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setImagePreview(base64);
      setBase64Image(base64);
    };
    reader.readAsDataURL(file);
  };

  //Lead owner
  const getLeadsOwner = async () => {
    try {
      // VAPT Security: Validate ID before API call
      if (!isValidId(id)) {
        return;
      }
      const response = await getLeadsOwnerApi(id);
      if (response.data.success === true) {
        // VAPT Security: Sanitize employee data
        const employee = response.data.employee;
        setLeadOwner({
          name: sanitizeInput(employee?.name ?? ""),
          ...employee,
        });
      }
      return response.data;
    } catch (err) {
      // Error handled internally
    } finally {
    }
  };

  useEffect(() => {
    getLeadsOwner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",

        backgroundColor: "#f8f9fb",
      }}
    >
      <div style={{ marginBottom: "32px" }}>
        <Row
          className="align-items-center justify-content-between"
          style={{ width: "100%" }}
        >
          <Col sm={12} md={6} lg={8}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <img
                src={Back}
                style={{ width: "40px" }}
                alt="Back"
                onClick={() => navigate(-1)}
              />
              <div
                style={{
                  fontWeight: "500",
                  fontSize: "20px",
                  color: "#172b4d",
                }}
              >
                New Lead
              </div>
            </div>
          </Col>

          <Col sm={12} md={6} lg={4}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                style={{
                  fontWeight: "500",
                  fontSize: "16px",
                  color: "#21272c",
                  height: "40px",
                  borderRadius: "100px",
                  border: "1px solid #d4dde7",
                  backgroundColor: "#ffffff",
                  padding: "10px 28px",
                  lineHeight: "100%",
                }}
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button
                style={{
                  fontWeight: "500",
                  fontSize: "16px",
                  color: "#ffffff",
                  height: "40px",
                  borderRadius: "100px",
                  backgroundColor: isSaving || isLoading ? "#7E8AFF" : "#3d4af5",
                  padding: "10px 28px",
                  lineHeight: "100%",
                  border: "none",
                  cursor: isSaving || isLoading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
                disabled={isSaving || isLoading}
                onClick={submitLead}
              >
                {isSaving ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ color: "#ffffff" }}
                  />
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </Col>
        </Row>
      </div>

      <div>
        <Row className="justify-content-between" style={{ width: "100%" }}>
          <Col sm={12} md={12} lg={12} xl={7}>
            <div
              style={{
                backgroundColor: "#ffffff",
                width: "100%",
                borderRadius: "24px",
                padding: "24px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                <Row>
                  <Col sm={4}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        marginBottom: "32px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "500",
                          fontSize: "20px",
                          color: "#172b4d",
                        }}
                      >
                        Lead information
                      </div>
                    </div>
                  </Col>
                  <Col sm={8}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "8px",
                        alignItems: "center",
                        justifyContent: "end",
                      }}
                    >
                      <span style={{ fontSize: "12px", fontWeight: "400" }}>
                        Lead Owner:
                      </span>
                      <span
                        style={{
                          backgroundColor: "#F8F8F8",
                          padding: "12px 12px",
                          width: "168px",
                          borderRadius: "10px",
                        }}
                      >
                        <LeadOwner style={{ marginRight: "5px" }} />
                        {leadOwner?.name || "—"}
                      </span>

                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col sm={12}>
                    <div
                      style={{
                        borderRadius: "16px",
                        padding: "16px",
                        border: "1px dashed #e5e5e5",
                      }}
                    >
                      <Row
                        style={{
                          alignItems: "center",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Col sm={12} md={8}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "20px",
                            }}
                          >
                            <div>
                              {(base64Image !== "" || profileUrl === "") && (
                                <img
                                  src={imagePreview}
                                  alt="Preview1"
                                  style={{
                                    width: "100px",
                                    height: "100px",
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                  }}
                                />
                              )}
                              {base64Image === "" && profileUrl !== "" && (
                                <img
                                  src={profileUrl}
                                  alt="Preview2"
                                  style={{
                                    width: "100px",
                                    height: "100px",
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                  }}
                                />
                              )}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "500",
                                  fontSize: "20px",
                                  color: "#212129",
                                }}
                              >
                                Upload photo
                              </div>
                              <div
                                style={{
                                  fontWeight: "400",
                                  fontSize: "14px",
                                  color: "#98a1b0",
                                }}
                              >
                                An image of the person, it's best if it has the
                                same length and height
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col sm={12} md={4}>
                          <div
                            onClick={() => fileInputRef.current.click()}
                            style={{
                              borderRadius: "100px",
                              border: "1px solid #e5e5e5",
                              padding: "10px 28px",
                              backgroundColor: "#ffffff",
                              fontWeight: "500",
                              fontSize: "16px",
                              color: "#3D4AF5",
                              lineHeight: "100%",
                              display: "flex",
                              justifyContent: "center",
                              width: "fit-content",
                              height: "fit-content",
                              cursor: "pointer",
                            }}
                          >
                            Choose files
                          </div>
                          <input
                            type="file"
                            accept="image/png, image/jpeg, image/jpg"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Row style={{ width: "100%" }}>
                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>
                            First Name{" "}
                            <span style={{ color: "#ff4444" }}>*</span>
                          </div>
                          <div>
                            <input
                              type="text"
                              value={firstName}
                              onChange={(e) =>
                                setFirstName(sanitizeTextarea(e.target.value))
                              }
                              style={{
                                height: "44px",
                                borderRadius: "10px",
                                padding: "16px",
                                border: "0.8px solid #e5e5e5",
                                backgroundColor: "#ffffff",
                                width: "100%",
                                fontWeight: "400",
                                fontSize: "14px",
                                color: "#172b4d",
                              }}
                              placeholder="Enter here"
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.first_name}
                            </span>
                          </div>
                        </div>
                      </Col>

                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Last Name</div>
                          <div>
                            <input
                              value={lastName}
                              type="text"
                              onChange={(e) =>
                                setLastName(sanitizeTextarea(e.target.value))
                              }
                              style={{
                                height: "44px",
                                borderRadius: "10px",
                                padding: "16px",
                                border: "0.8px solid #e5e5e5",
                                backgroundColor: "#ffffff",
                                width: "100%",
                                fontWeight: "400",
                                fontSize: "14px",
                                color: "#172b4d",
                              }}
                              placeholder="Enter Here"
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.last_name}
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Row>

                <Row>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Row style={{ width: "100%" }}>
                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Lead Assignee</div>
                          <div>
                            <SearchWithFreeTextAPI
                              onDropdownChange={
                                handleLeadAssigneeDropdownChange
                              }
                              onChangeSearch={handleLeadAssigneeOnChange}
                              apiUrl={apiAccUrl + `get_employee`}
                              params={{}}
                              searchLimit={0}
                              placeholder={"Search Employee"}
                              selectedValue={leadAssignee}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Email</div>
                          <div>
                            <input
                              type="email"
                              value={email}
                              onChange={handleEmailChange}
                              style={{
                                height: "44px",
                                borderRadius: "10px",
                                padding: "16px",
                                border: "0.8px solid #e5e5e5",
                                backgroundColor: "#ffffff",
                                width: "100%",
                                fontWeight: "400",
                                fontSize: "14px",
                                color: "#172b4d",
                              }}
                              placeholder="Enter Here"
                            />

                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.email}
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Row>

                <Row>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Row style={{ width: "100%" }}>
                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Mobile Number</div>
                          <div>
                            <input
                              type="text"
                              value={mobileNumber}
                              onChange={(e) => {
                                const value = e.target.value
                                  .replace(/[^0-9]/g, "")
                                  .slice(0, 15);
                                setMobileNumber(value);
                              }}
                              style={{
                                height: "44px",
                                borderRadius: "10px",
                                padding: "16px",
                                border: "0.8px solid #e5e5e5",
                                backgroundColor: "#ffffff",
                                width: "100%",
                                fontWeight: "400",
                                fontSize: "14px",
                                color: "#172b4d",
                              }}
                              placeholder="Enter here"
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.contact_number1}
                            </span>
                          </div>
                        </div>
                      </Col>

                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Secondary Mobile Number</div>
                          <div>
                            <input
                              type="text"
                              value={secondaryMobileNumber}
                              onChange={(e) => {
                                const value = e.target.value
                                  .replace(/[^0-9]/g, "")
                                  .slice(0, 15);
                                setSecondaryMobileNumber(value);
                              }}
                              style={{
                                height: "44px",
                                borderRadius: "10px",
                                padding: "16px",
                                border: "0.8px solid #e5e5e5",
                                backgroundColor: "#ffffff",
                                width: "100%",
                                fontWeight: "400",
                                fontSize: "14px",
                                color: "#172b4d",
                              }}
                              placeholder="Enter Here"
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.contact_number2}
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Row>

                <Row>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Row style={{ width: "100%" }}>
                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Fax</div>
                          <div>
                            <input
                              type="text"
                              value={fax}
                              onChange={(e) =>
                                setFax(sanitizeTextarea(e.target.value))
                              }
                              style={{
                                height: "44px",
                                borderRadius: "10px",
                                padding: "16px",
                                border: "0.8px solid #e5e5e5",
                                backgroundColor: "#ffffff",
                                width: "100%",
                                fontWeight: "400",
                                fontSize: "14px",
                                color: "#172b4d",
                              }}
                              placeholder="Enter here"
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.fax}
                            </span>
                          </div>
                        </div>
                      </Col>
                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Lead Status</div>
                          <div>
                            <SearchWithFreeTextAPI
                              onDropdownChange={handleLeadStatusDropdownChange}
                              onChangeSearch={handleLeadStatusOnChange}
                              apiUrl={apiUrl + `lead/leadstatus_autosuggestion`}
                              params={{}}
                              searchLimit={0}
                              placeholder={"Search Lead Status"}
                              selectedValue={leadStatus}
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.lead_status}
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Row>

                <Row>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Row style={{ width: "100%" }}>
                      <Col sm={12}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Company Name</div>
                          <div>
                            <input
                              type="text"
                              value={company}
                              onChange={(e) =>
                                setCompany(sanitizeTextarea(e.target.value))
                              }
                              style={{
                                height: "44px",
                                borderRadius: "10px",
                                padding: "16px",
                                border: "0.8px solid #e5e5e5",
                                backgroundColor: "#ffffff",
                                width: "100%",
                                fontWeight: "400",
                                fontSize: "14px",
                                color: "#172b4d",
                              }}
                              placeholder="Enter here"
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.company_name}
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Row>

                <Row>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Row style={{ width: "100%" }}>
                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Industry</div>
                          <div>
                            <SearchWithFreeTextAPI
                              onDropdownChange={handleIndustryDropdownChange}
                              onChangeSearch={handleIndustryOnChange}
                              apiUrl={apiUrl + `lead/industry_autosuggestion`}
                              params={{}}
                              searchLimit={0}
                              placeholder={"Search Industry"}
                              selectedValue={industry}
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.industry}
                            </span>
                          </div>
                        </div>
                      </Col>

                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Lead Source</div>
                          <div>
                            <SearchWithFreeTextAPI
                              onDropdownChange={handleLeadSourceDropdownChange}
                              onChangeSearch={handleLeadSourceOnChange}
                              apiUrl={apiUrl + `lead/leadsource_autosuggestion`}
                              params={{}}
                              searchLimit={0}
                              placeholder={"Search Lead Source"}
                              selectedValue={leadSource}
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.lead_source}
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Row>

                <Row>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Row style={{ width: "100%" }}>
                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>Annual Revenue</div>
                          <div>
                            <input
                              type="number"
                              value={annualReve}
                              onChange={(e) =>
                                setAnnualReve(
                                  sanitizeNumericInput(
                                    e.target.value,
                                    MAX_NUMERIC_VALUE,
                                  ),
                                )
                              }
                              style={{
                                height: "44px",
                                borderRadius: "10px",
                                border: "0.6px solid #d4dde7",
                                backgroundColor: "#ffffff",
                                padding: "16px 12px",
                                width: "100%",
                                fontWeight: "400",
                                fontSize: "14px",
                                color: "#172b4d",
                              }}
                              placeholder="Enter here..."
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.annual_revenue}
                            </span>
                          </div>
                        </div>
                      </Col>
                      <Col sm={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div>No. of Employees</div>
                          <div>
                            <input
                              type="number"
                              value={employees}
                              onChange={(e) =>
                                setEmployees(
                                  sanitizeNumericInput(
                                    e.target.value,
                                    MAX_NUMERIC_VALUE,
                                  ),
                                )
                              }
                              style={{
                                height: "44px",
                                borderRadius: "10px",
                                border: "0.6px solid #d4dde7",
                                backgroundColor: "#ffffff",
                                padding: "16px 12px",
                                width: "100%",
                                fontWeight: "400",
                                fontSize: "14px",
                                color: "#172b4d",
                              }}
                              placeholder="Enter here..."
                            />
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.emp_count}
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Row>
              </div>
            </div>
          </Col>
          <Col sm={12} md={12} lg={12} xl={5}>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <Row>
                <Col sm={12}>
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      width: "100%",
                      borderRadius: "24px",
                      padding: "24px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "500",
                        fontSize: "20px",
                        color: "#172b4d",
                        marginBottom: "32px",
                      }}
                    >
                      Address information
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "400",
                            fontSize: "14px",
                            color: "#21272c",
                          }}
                        >
                          Street
                        </div>
                        <div>
                          <input
                            type="text"
                            value={street}
                            onChange={(e) =>
                                setStreet(sanitizeTextarea(e.target.value))
                            }
                            style={{
                              height: "44px",
                              borderRadius: "10px",
                              border: "0.6px solid #d4dde7",
                              backgroundColor: "#ffffff",
                              padding: "16px 12px",
                              width: "100%",
                              fontWeight: "400",
                              fontSize: "14px",
                              color: "#172b4d",
                            }}
                            placeholder="Enter here..."
                          />
                          <span
                            style={{
                              color: "#ff4444",
                              fontSize: "12px",
                              fontWeight: "400",
                            }}
                          >
                            {validations.street}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "400",
                            fontSize: "14px",
                            color: "#21272c",
                          }}
                        >
                          City
                        </div>
                        <div>
                          <SearchWithAPI
                            onDropdownChange={handleCityDropdownChange}
                            apiUrl={apiAccUrl + "get_city"}
                            params={{
                              state_id:
                                stateOption != null ? stateOption.value : "",
                            }}
                            placeholder={"Select"}
                            selectedOption={cityOption}
                          />
                          <span
                            style={{
                              color: "#ff4444",
                              fontSize: "12px",
                              fontWeight: "400",
                            }}
                          >
                            {validations.city_id}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "400",
                            fontSize: "14px",
                            color: "#21272c",
                          }}
                        >
                          State
                        </div>
                        <div>
                          <SearchWithAPI
                            onDropdownChange={handleStateDropdownChange}
                            apiUrl={apiAccUrl + "get_state"}
                            params={{
                              country_id:
                                countryOption != null
                                  ? countryOption.value
                                  : "",
                            }}
                            placeholder={"Select"}
                            selectedOption={stateOption}
                          />
                          <span
                            style={{
                              color: "#ff4444",
                              fontSize: "12px",
                              fontWeight: "400",
                            }}
                          >
                            {validations.state_id}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "400",
                            fontSize: "14px",
                            color: "#21272c",
                          }}
                        >
                          Country
                        </div>
                        <div>
                          <SearchWithAPI
                            onDropdownChange={handleCountryDropdownChange}
                            apiUrl={apiAccUrl + "get_country"}
                            params={{}}
                            placeholder={"Select"}
                            selectedOption={countryOption}
                          />
                          <span
                            style={{
                              color: "#ff4444",
                              fontSize: "12px",
                              fontWeight: "400",
                            }}
                          >
                            {validations.country_id}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "400",
                            fontSize: "14px",
                            color: "#21272c",
                          }}
                        >
                          Zip Code
                        </div>
                        <div>
                          <input
                            type="text"
                            value={zipCode}
                            onChange={(e) =>
                                setZipCode(sanitizeTextarea(e.target.value))
                            }
                            style={{
                              height: "44px",
                              borderRadius: "10px",
                              border: "0.6px solid #d4dde7",
                              backgroundColor: "#ffffff",
                              padding: "16px 12px",
                              width: "100%",
                              fontWeight: "400",
                              fontSize: "14px",
                              color: "#172b4d",
                            }}
                            placeholder="Enter here..."
                          />
                          <span
                            style={{
                              color: "#ff4444",
                              fontSize: "12px",
                              fontWeight: "400",
                            }}
                          >
                            {validations.zip_code}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row
                style={{
                  backgroundColor: "#ffffff",
                  width: "100%",
                  borderRadius: "24px",
                  padding: "12px",
                  height: "50px",
                }}
              >
                <Col sm={6}>
                  <div>
                    <div
                      onClick={() => setMailChecked(!mailChecked)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                          color: "#172B4D",
                        }}
                      >
                        Email Marketing
                      </span>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div
                    onClick={() => setMailChecked(!mailChecked)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      justifyContent: "end",
                    }}
                  >
                    <div
                      style={{
                        width: "18px",
                        height: "18px",
                        borderRadius: "4px",
                        border: `1px solid ${mailChecked ? "#3D6DFF" : "#8790A1"}`,
                        backgroundColor: mailChecked
                          ? "#3D6DFF"
                          : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "0.2s",
                      }}
                    >
                      {mailChecked && (
                        <span
                          style={{
                            color: "white",
                            fontSize: "12px",
                            fontWeight: "700",
                            lineHeight: 0,
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* <Row>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Row style={{ width: "100%" }}>
                    <div style={{ backgroundColor: "#ffffff", width: "100%", borderRadius: "24px", padding: "12px" }}>
                    <Col sm={12} md={6}>
                      
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <div></div>
                          <div
                            onClick={() => setMailChecked(!mailChecked)}
                            style={{
                              marginTop: '32px',
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              cursor: "pointer",
                            }}
                          >
                            <div
                              style={{
                                width: "18px",
                                height: "18px",
                                borderRadius: "4px",
                                border: `1px solid ${mailChecked ? "#3D6DFF" : "#8790A1"}`,
                                backgroundColor: mailChecked ? "#3D6DFF" : "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "0.2s",
                              }}
                            >
                              {mailChecked && (
                                <span
                                  style={{
                                    color: "white",
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    lineHeight: 0,
                                  }}
                                >
                                  ✓
                                </span>
                              )}
                            </div>

                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: 400,
                                color: "#172B4D",
                              }}
                            >
                              Email Marketing
                            </span>
                          </div>
                        </div>
                      
                    </Col>
                    </div>
                  </Row>
                </div>
              </Row> */}

              <Row>
                <Col sm={12}>
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      width: "100%",
                      borderRadius: "24px",
                      padding: "24px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "500",
                        fontSize: "20px",
                        color: "#172b4d",
                        marginBottom: "16px",
                      }}
                    >
                      Description
                    </div>

                    <div>
                      <textarea
                        value={description}
                        onChange={(e) =>
                          setDescription(sanitizeTextarea(e.target.value))
                        }
                        style={{
                          minHeight: "160px",
                          maxHeight: "auto",
                          borderRadius: "10px",
                          border: "0.6px solid #d4dde7",
                          backgroundColor: "#ffffff",
                          padding: "16px 12px",
                          width: "100%",
                          fontWeight: "400",
                          fontSize: "14px",
                          color: "#172b4d",
                          resize: "vertical",
                          overflow: "auto",
                        }}
                        placeholder="Enter here..."
                      ></textarea>
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.description}
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
          <Col sm={12} md={12} lg={12} xl={12}>
            <Row>
              <Col sm={12}>
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    width: "100%",
                    borderRadius: "24px",
                    padding: "24px",
                    marginTop: "12px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "500",
                      fontSize: "20px",
                      color: "#172b4d",
                      marginBottom: "24px",
                    }}
                  >
                    Website
                  </div>

                  {/* Website URL Input */}
                  <div style={{ marginBottom: "16px" }}>
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: "16px",
                        color: "#172B4D",
                        marginBottom: "8px",
                      }}
                    >
                      Website
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <input
                          type="url"
                          value={websiteUrl}
                          onFocus={() => setIsActive(true)}
                          onBlur={() => setIsActive(false)}
                          disabled={isInputDisabled}
                          onChange={(e) => {
                            const value = e.target.value;
                            setWebsiteUrl(value);
                            // URL validation will happen on submit
                          }}
                          style={{
                            height: "40px",
                            borderRadius: "10px",
                            border: isActive
                              ? "1px solid #3D6DFF"
                              : "0.6px solid #d4dde7",
                            backgroundColor: isInputDisabled
                              ? "#F8F8F8"
                              : "#ffffff",
                            padding: "0px 38px 0px 12px",
                            width: "100%",
                            fontWeight: "400",
                            fontSize: "14px",
                            color: "#172b4d",
                            outline: "none",
                          }}
                          placeholder="https://example.com"
                        />

                        {errorMsg.url && (
                          <span
                            style={{
                              marginTop: "4px",
                              color: "#ff4444",
                              fontSize: "12px",
                              fontWeight: "400",
                            }}
                          >
                            {errorMsg.url}
                          </span>
                        )}
                      </div>

                      {/* ❌ Clear Button */}
                      {websiteUrl && (
                        <button
                          onClick={() => {
                            clearScrappedData();
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                            padding: "8px 20px",
                            backgroundColor: "#FFFFFF",
                            color: "#21272C",
                            borderRadius: "10px",
                            fontSize: "13px",
                            fontWeight: "500",
                            cursor: "pointer",
                            outline: "none",
                            transition: "0.2s",
                            whiteSpace: "nowrap",
                            border: "1px solid #E5E5E5",
                          }}
                        >
                          Clear
                        </button>
                      )}

                      <button
                        onClick={getWebScrapDet}
                        disabled={isLoading}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          padding: "8px 20px",
                          backgroundColor: isLoading ? "#8fa8ff" : "#3D6DFF",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "10px",
                          fontSize: "13px",
                          fontWeight: "500",
                          cursor: isLoading ? "not-allowed" : "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isLoading ? (
                          <Spinner
                            animation="border"
                            size="sm"
                            style={{ color: "white" }}
                          />
                        ) : (
                          <>
                            <SearchIc /> Search
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <WebsiteDetailsWidget
                      webScrapDetails={webScrapDetails}
                      sections={sections}
                      socialLinks={socialLinks}
                      serviceList={serviceList}
                      industriesList={industriesList}
                      productsList={productsList}
                      teamList={teamList}
                      partnerList={partnerList}
                      payLinkDetails={payLinkDetails}
                      payLinkList={payLinkList}
                      aboutList={aboutList}
                      clientList={clientList}
                      peopleList={peopleList}
                      careersList={careersList}
                      sourcePage="create"
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default LeadsAdd;
